import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Card from '../common/Card';
import { axiosInstance } from '../../../config/dataProvider';
import moment from 'moment';
import {
  PersonAdd,
  Login,
  AccessTime,
  MonitorWeight,
  LocalDrink,
  DirectionsWalk,
  Restaurant,
  Refresh,
  Chat,
  List,
} from '@mui/icons-material';
import { Tabs, Tab, Box } from '@mui/material';

const Container = styled(Card)`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  height: fit-content;
  max-height: 800px;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
  flex-shrink: 0;
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs} ${props => getTheme(props.theme?.isDark).spacing.sm};
  background: ${props => getTheme(props.theme?.isDark).colors.surface};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background: ${props => getTheme(props.theme?.isDark).colors.hover};
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const StyledTabs = styled(Tabs)`
  border-bottom: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.sm};

  .MuiTab-root {
    color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
    font-size: 13px;
    text-transform: none;
    min-height: 40px;
    
    &.Mui-selected {
      color: ${props => getTheme(props.theme?.isDark).colors.primary};
    }
  }

  .MuiTabs-indicator {
    background-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const TabPanel = styled(Box)`
  flex: 1;
  min-height: 0;
  max-height: 600px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => getTheme(props.theme?.isDark).colors.surface};
    border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => getTheme(props.theme?.isDark).colors.border};
    border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
    
    &:hover {
      background: ${props => getTheme(props.theme?.isDark).colors.primary};
    }
  }
`;

const ChatScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  justify-content: flex-end;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  flex: 1;
  min-height: 0;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  position: relative;
  padding-left: 40px;
  min-height: 40px;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
  padding-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 14px;
    top: 30px;
    bottom: -${props => getTheme(props.theme?.isDark).spacing.md};
    width: 2px;
    background: ${props => getTheme(props.theme?.isDark).colors.border};
  }
`;

const IconContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => {
    const type = props.$activityType;
    if (type === 'REGISTRATION') return getTheme(props.theme?.isDark).colors.primary;
    if (type === 'LOGIN' || type === 'LAST_LOGIN') return getTheme(props.theme?.isDark).colors.info || '#2196F3';
    if (type === 'WEIGHT_LOG') return getTheme(props.theme?.isDark).colors.warning || '#FF8C39';
    if (type === 'WATER_LOG') return '#2196F3';
    if (type === 'WALKING_LOG') return getTheme(props.theme?.isDark).colors.success || '#19B888';
    if (type === 'MEAL_LOG') return '#FF6B6B';
    return getTheme(props.theme?.isDark).colors.border;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 2;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateX(0);
`;

const Content = styled.div`
  flex: 1;
  padding-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
  padding-top: 0;
  min-width: 0;
  margin-left: 0;
`;

const ActivityTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-bottom: 2px;
`;

const ActivityDescription = styled.div`
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  margin-bottom: 2px;
  padding-left: 8px;
`;

const ActivityTime = styled.div`
  font-size: 11px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  opacity: 0.8;
  margin-top: 2px;
`;

const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  margin: ${props => getTheme(props.theme?.isDark).spacing.md} 0 ${props => getTheme(props.theme?.isDark).spacing.sm} 0;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs} 0;
  position: relative;
  
  &::before {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => getTheme(props.theme?.isDark).colors.border};
  }
  
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => getTheme(props.theme?.isDark).colors.border};
  }
`;

const DateSeparatorText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  padding: 0 ${props => getTheme(props.theme?.isDark).spacing.sm};
  background: ${props => getTheme(props.theme?.isDark).colors.background || getTheme(props.theme?.isDark).colors.surface};
`;

const LogItem = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border-bottom: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const LogIconContainer = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => {
    const type = props.$logType;
    if (type === 'MEAL_LOG') return '#FF6B6B';
    if (type === 'WEIGHT_LOG') return getTheme(props.theme?.isDark).colors.warning || '#FF8C39';
    if (type === 'WATER_LOG') return '#2196F3';
    if (type === 'WALKING_LOG') return getTheme(props.theme?.isDark).colors.success || '#19B888';
    return getTheme(props.theme?.isDark).colors.border;
  }};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const LogContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const LogTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const LogDetails = styled.div`
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  margin-bottom: 2px;
`;

const LogTime = styled.div`
  font-size: 11px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  opacity: 0.8;
  margin-top: 4px;
`;


const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  width: 100%;
`;

const ChatMessage = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 75%;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
  align-self: ${props => props.$isCustomer ? 'flex-start' : 'flex-end'};
  margin-left: ${props => props.$isCustomer ? '0' : 'auto'};
  margin-right: ${props => props.$isCustomer ? 'auto' : '0'};
`;

const ChatBubble = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm} ${props => getTheme(props.theme?.isDark).spacing.md};
  border-radius: ${props => props.$isCustomer ? '16px 16px 16px 4px' : '16px 16px 4px 16px'};
  background: ${props => {
    if (props.$isCustomer) {
      return getTheme(props.theme?.isDark).colors.surface;
    } else {
      return getTheme(props.theme?.isDark).colors.primary || '#007bff';
    }
  }};
  color: ${props => {
    if (props.$isCustomer) {
      return getTheme(props.theme?.isDark).colors.text;
    } else {
      return '#ffffff';
    }
  }};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
`;

const ChatText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 4px;
`;

const ChatMeta = styled.div`
  display: flex;
  justify-content: ${props => props.$isCustomer ? 'flex-start' : 'flex-end'};
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.xs};
  margin-top: 4px;
  padding: 0 ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const ChatSender = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  opacity: 0.8;
`;

const ChatTime = styled.div`
  font-size: 10px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  opacity: 0.7;
`;


const EmptyState = styled.div`
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  font-style: italic;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const getIcon = (type) => {
  const iconMap = {
    REGISTRATION: PersonAdd,
    LOGIN: Login,
    LAST_LOGIN: AccessTime,
    WEIGHT_LOG: MonitorWeight,
    WATER_LOG: LocalDrink,
    WALKING_LOG: DirectionsWalk,
    MEAL_LOG: Restaurant,
  };
  return iconMap[type] || PersonAdd;
};

const getLogIcon = (type) => {
  const iconMap = {
    MEAL_LOG: Restaurant,
    WEIGHT_LOG: MonitorWeight,
    WATER_LOG: LocalDrink,
    WALKING_LOG: DirectionsWalk,
  };
  return iconMap[type] || List;
};

const CustomerActivityTimeline = ({ customerId }) => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [tabValue, setTabValue] = useState(0);
  const [activities, setActivities] = useState([]);
  const [logs, setLogs] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState({
    activities: true,
    logs: false,
    chat: false,
  });
  const [conversationId, setConversationId] = useState(null);
  const chatEndRef = useRef(null);
  const tabPanelRef = useRef(null);

  const fetchActivities = async () => {
    try {
      setLoading(prev => ({ ...prev, activities: true }));
      const response = await axiosInstance.get(`/customers/${customerId}`);
      const activitiesData = response.data.activities || [];
      
      const transformedActivities = activitiesData.map((activity) => {
        let type = 'OTHER';
        if (activity.activityType === 'APP_OPENED') type = 'LOGIN';
        else if (activity.activityType === 'APP_CLOSED') type = 'LAST_LOGIN';
        else if (activity.activityType === 'SCREEN_VIEWED') type = 'LOGIN';
        else if (activity.activityType === 'BUTTON_CLICKED') type = 'LOGIN';
        else if (activity.activityType === 'WEIGHT_LOG_CREATED') type = 'WEIGHT_LOG';
        else if (activity.activityType === 'WATER_LOG_CREATED') type = 'WATER_LOG';
        else if (activity.activityType === 'WALKING_LOG_UPDATED') type = 'WALKING_LOG';
        else if (activity.activityType === 'MEAL_LOG_CREATED') type = 'MEAL_LOG';
        
        let title = activity.activityType;
        let description = '';
        
        if (activity.activityType === 'SCREEN_VIEWED' && activity.screenName) {
          title = 'Səhifə açıldı';
          description = activity.screenName;
        } else if (activity.activityType === 'BUTTON_CLICKED' && activity.action) {
          title = 'Düyməyə klik edildi';
          description = activity.action;
        } else if (activity.activityType === 'APP_OPENED') {
          title = 'Tətbiq açıldı';
        } else if (activity.activityType === 'APP_CLOSED') {
          title = 'Tətbiq bağlandı';
        }
        
        return {
          type,
          title,
          description,
          date: activity.createdAt || activity.date,
        };
      });
      
      setActivities(transformedActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setActivities([]);
    } finally {
      setLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(prev => ({ ...prev, logs: true }));
      
      // Fetch customer data which includes logs
      const response = await axiosInstance.get(`/customers/${customerId}`);
      const logsData = response.data.logs || {};

      const allLogs = [
        ...(logsData.mealLogs || []).map(log => ({
          type: 'MEAL_LOG',
          title: 'Yemək log-u',
          data: log,
          date: log.date || log.createdAt,
        })),
        ...(logsData.weightLogs || []).map(log => ({
          type: 'WEIGHT_LOG',
          title: 'Çəki log-u',
          data: log,
          date: log.date || log.createdAt,
        })),
        ...(logsData.waterLogs || []).map(log => ({
          type: 'WATER_LOG',
          title: 'Su log-u',
          data: log,
          date: log.date || log.createdAt,
        })),
        ...(logsData.walkingLogs || []).map(log => ({
          type: 'WALKING_LOG',
          title: 'Gəzinti log-u',
          data: log,
          date: log.date || log.createdAt,
        })),
      ];

      // Sort by date descending
      allLogs.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setLogs(allLogs);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs([]);
    } finally {
      setLoading(prev => ({ ...prev, logs: false }));
    }
  };

  const fetchChatMessages = async () => {
    try {
      setLoading(prev => ({ ...prev, chat: true }));
      
      // First, try to get conversation for this customer
      let convId = conversationId;
      
      if (!convId) {
        // Try to get conversation from conversations list
        try {
          const conversationsResponse = await axiosInstance.get('/assistant/conversations?page=1&limit=1000');
          const conversationsData = conversationsResponse.data;
          // Handle both 'results' (paginate plugin) and 'docs' (aggregatePaginate) formats
          const conversations = conversationsData?.results || conversationsData?.docs || (Array.isArray(conversationsData) ? conversationsData : []);
          
          // Find conversation by customer ID (handle both string and object formats)
          const customerConversation = conversations.find(conv => {
            const convCustomerId = conv.customer?._id || conv.customer?.id || conv.customer;
            const customerIdStr = String(customerId);
            const convCustomerIdStr = String(convCustomerId);
            const matches = convCustomerIdStr === customerIdStr;
            if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production' && matches) {
              console.log('Found conversation:', conv, 'for customer:', customerId);
            }
            return matches;
          });
          
          if (customerConversation) {
            convId = customerConversation._id || customerConversation.id;
            setConversationId(convId);
            if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
              console.log('Using conversation ID:', convId);
            }
          } else {
            if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
              console.log('No conversation found for customer:', customerId);
              console.log('Available conversations:', conversations.map(c => ({
                id: c._id || c.id,
                customer: c.customer?._id || c.customer?.id || c.customer
              })));
            }
            setChatMessages([]);
            return;
          }
        } catch (err) {
          console.error('Error fetching conversations:', err);
          setChatMessages([]);
          return;
        }
      }

      if (convId) {
        try {
          const messagesResponse = await axiosInstance.get(`/assistant/conversations/messages?conversation=${convId}&page=1&limit=100`);
          const messagesData = messagesResponse.data;
          
          if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
            console.log('Messages response:', messagesData);
            console.log('Messages response type:', typeof messagesData);
            console.log('Is array:', Array.isArray(messagesData));
            console.log('Has docs:', !!messagesData?.docs);
            console.log('Has data:', !!messagesData?.data);
            console.log('Keys:', messagesData ? Object.keys(messagesData) : 'null');
          }
          
          // Handle different response formats
          // Backend paginate plugin returns 'results' field, not 'docs'
          let messages = [];
          if (Array.isArray(messagesData)) {
            messages = messagesData;
          } else if (messagesData?.results && Array.isArray(messagesData.results)) {
            messages = messagesData.results;
          } else if (messagesData?.docs && Array.isArray(messagesData.docs)) {
            messages = messagesData.docs;
          } else if (messagesData?.data && Array.isArray(messagesData.data)) {
            messages = messagesData.data;
          }
          
          if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
            console.log('Parsed messages:', messages);
            console.log('Messages count:', messages.length);
          }
          
          // Sort messages by date (oldest first - ascending)
          messages.sort((a, b) => {
            const dateA = new Date(a.createdAt || a.date);
            const dateB = new Date(b.createdAt || b.date);
            return dateA - dateB; // Oldest first
          });
          
          setChatMessages(messages);
        } catch (err) {
          console.error('Error fetching messages:', err);
          if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
            console.error('Error details:', err.response?.data || err.message);
            console.error('Error response:', err.response);
          }
          setChatMessages([]);
        }
      } else {
        setChatMessages([]);
      }
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      setChatMessages([]);
    } finally {
      setLoading(prev => ({ ...prev, chat: false }));
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  useEffect(() => {
    if (customerId && tabValue === 1) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, tabValue]);

  useEffect(() => {
    if (customerId && tabValue === 2) {
      fetchChatMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, tabValue]);

  // Scroll to bottom when chat messages are loaded or tab changes to chat
  useEffect(() => {
    if (tabValue === 2) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        // Scroll the tab panel to bottom
        if (tabPanelRef.current) {
          tabPanelRef.current.scrollTop = tabPanelRef.current.scrollHeight;
        }
      }, 150);
    }
  }, [chatMessages, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatTime = (date) => {
    return moment(date).format('HH:mm');
  };

  const formatDateHeader = (date) => {
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    const dateMoment = moment(date).startOf('day');
    
    if (dateMoment.isSame(today, 'day')) {
      return 'Bu gün';
    } else if (dateMoment.isSame(yesterday, 'day')) {
      return 'Dünən';
    } else {
      return moment(date).format('DD MMMM YYYY');
    }
  };

  const groupByDate = (items) => {
    const grouped = {};
    items.forEach((item) => {
      const dateKey = moment(item.date || item.createdAt).startOf('day').format('YYYY-MM-DD');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(item);
    });
    return grouped;
  };

  const renderActivityTab = () => {
    if (loading.activities) {
      return <LoadingState theme={theme}>Yüklənir...</LoadingState>;
    }

    if (activities.length === 0) {
      return <EmptyState theme={theme}>Aktivlik tapılmadı</EmptyState>;
    }

    const grouped = groupByDate(activities);
    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));

    return (
      <Timeline theme={theme}>
        {sortedDates.map((dateKey, dateIndex) => {
          const dateItems = grouped[dateKey];
          const firstItem = dateItems[0];
          
          return (
            <React.Fragment key={dateKey}>
              <DateSeparator theme={theme}>
                <DateSeparatorText theme={theme}>
                  {formatDateHeader(firstItem.date)}
                </DateSeparatorText>
              </DateSeparator>
              {dateItems.map((activity, index) => {
                const Icon = getIcon(activity.type);
                return (
                  <TimelineItem key={`${dateKey}-${index}`} theme={theme}>
                    <IconContainer $activityType={activity.type} theme={theme}>
                      <Icon style={{ fontSize: 14 }} />
                    </IconContainer>
                    <Content theme={theme}>
                      <ActivityTitle theme={theme}>{activity.title}</ActivityTitle>
                      <ActivityDescription theme={theme}>{activity.description}</ActivityDescription>
                      <ActivityTime theme={theme}>{formatTime(activity.date)}</ActivityTime>
                    </Content>
                  </TimelineItem>
                );
              })}
            </React.Fragment>
          );
        })}
      </Timeline>
    );
  };

  const renderLogsTab = () => {
    if (loading.logs) {
      return <LoadingState theme={theme}>Yüklənir...</LoadingState>;
    }

    if (logs.length === 0) {
      return <EmptyState theme={theme}>Log tapılmadı</EmptyState>;
    }

    const grouped = groupByDate(logs);
    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));

    return (
      <div>
        {sortedDates.map((dateKey) => {
          const dateItems = grouped[dateKey];
          const firstItem = dateItems[0];
          
          return (
            <React.Fragment key={dateKey}>
              <DateSeparator theme={theme}>
                <DateSeparatorText theme={theme}>
                  {formatDateHeader(firstItem.date)}
                </DateSeparatorText>
              </DateSeparator>
              {dateItems.map((log, index) => {
                const LogIcon = getLogIcon(log.type);
                return (
                  <LogItem key={`${dateKey}-${index}`} theme={theme}>
                    <LogIconContainer theme={theme} $logType={log.type}>
                      <LogIcon style={{ fontSize: 18 }} />
                    </LogIconContainer>
                    <LogContent theme={theme}>
                      <LogTitle theme={theme}>{log.title}</LogTitle>
                      <LogDetails theme={theme}>
                        {log.type === 'MEAL_LOG' && log.data.food && (
                          <div>Yemək: {typeof log.data.food === 'object' ? (log.data.food.name || log.data.food.title) : log.data.food}</div>
                        )}
                        {log.type === 'MEAL_LOG' && log.data.quantity && (
                          <div>Miqdar: {log.data.quantity}</div>
                        )}
                        {log.type === 'WEIGHT_LOG' && log.data.weight && (
                          <div>Çəki: {log.data.weight} kg</div>
                        )}
                        {log.type === 'WATER_LOG' && log.data.quantity && (
                          <div>Miqdar: {log.data.quantity} ml</div>
                        )}
                        {log.type === 'WALKING_LOG' && log.data.count && (
                          <div>Addımlar: {log.data.count}</div>
                        )}
                      </LogDetails>
                      <LogTime theme={theme}>{formatTime(log.date)}</LogTime>
                    </LogContent>
                  </LogItem>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderChatTab = () => {
    if (loading.chat) {
      return <LoadingState theme={theme}>Yüklənir...</LoadingState>;
    }

    if (!Array.isArray(chatMessages) || chatMessages.length === 0) {
      return <EmptyState theme={theme}>Chat mesajı tapılmadı</EmptyState>;
    }

    const grouped = groupByDate(chatMessages);
    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b)); // Oldest first for chat

    return (
      <ChatScrollContainer>
        <ChatContainer theme={theme}>
          {sortedDates.map((dateKey) => {
            const dateItems = grouped[dateKey];
            const firstItem = dateItems[0];
            const isLastDate = dateKey === sortedDates[sortedDates.length - 1];
            
            return (
              <React.Fragment key={dateKey}>
                <DateSeparator theme={theme}>
                  <DateSeparatorText theme={theme}>
                    {formatDateHeader(firstItem.createdAt || firstItem.date)}
                  </DateSeparatorText>
                </DateSeparator>
                {dateItems.map((message, index) => {
                  // Backend uses senderType field, not sender
                  const isCustomer = message.senderType === 'customer';
                  const isLastMessage = isLastDate && index === dateItems.length - 1;
                  return (
                    <div key={`${dateKey}-${index}`} ref={isLastMessage ? chatEndRef : null}>
                      <ChatMessage 
                        theme={theme}
                        $isCustomer={isCustomer}
                      >
                        <ChatBubble theme={theme} $isCustomer={isCustomer}>
                          <ChatText theme={theme}>{message.message}</ChatText>
                        </ChatBubble>
                        <ChatMeta theme={theme} $isCustomer={isCustomer}>
                          <ChatSender theme={theme}>
                            {isCustomer ? 'İstifadəçi' : 'Dəstək'}
                          </ChatSender>
                          <ChatTime theme={theme}>
                            {formatTime(message.createdAt || message.date)}
                          </ChatTime>
                        </ChatMeta>
                      </ChatMessage>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </ChatContainer>
      </ChatScrollContainer>
    );
  };

  return (
    <Container theme={theme}>
      <Header>
        <Title theme={theme}>İstifadəçi Aktivlikləri</Title>
        <RefreshButton 
          onClick={() => {
            if (tabValue === 0) fetchActivities();
            else if (tabValue === 1) fetchLogs();
            else if (tabValue === 2) fetchChatMessages();
          }} 
          theme={theme}
        >
          <Refresh style={{ fontSize: 16 }} />
          Yenilə
        </RefreshButton>
      </Header>
      
      <StyledTabs 
        value={tabValue} 
        onChange={handleTabChange}
        theme={theme}
        sx={{
          '& .MuiTab-root': {
            color: isDark ? '#fff' : '#000',
            '&.Mui-selected': {
              color: getTheme(isDark).colors.primary,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: getTheme(isDark).colors.primary,
          },
        }}
      >
        <Tab label="Activity" icon={<AccessTime style={{ fontSize: 16 }} />} iconPosition="start" />
        <Tab label="Logs" icon={<List style={{ fontSize: 16 }} />} iconPosition="start" />
        <Tab label="Chat" icon={<Chat style={{ fontSize: 16 }} />} iconPosition="start" />
      </StyledTabs>

      <TabPanel theme={theme} ref={tabPanelRef}>
        {tabValue === 0 && renderActivityTab()}
        {tabValue === 1 && renderLogsTab()}
        {tabValue === 2 && renderChatTab()}
      </TabPanel>
    </Container>
  );
};

export default CustomerActivityTimeline;
